import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../services/api';

interface User {
  user_id: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Проверяем сохраненную сессию при загрузке
    const savedUser = localStorage.getItem('user');
    console.log('AuthContext: Загружаем пользователя из localStorage:', savedUser);
    
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('AuthContext: Парсим данные пользователя:', userData);
        setUser(userData);
      } catch (error) {
        console.error('AuthContext: Ошибка при парсинге пользователя:', error);
        localStorage.removeItem('user');
      }
    } else {
      console.log('AuthContext: Пользователь не найден в localStorage');
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('AuthContext: Попытка входа для:', email);
      const response = await apiClient.post('/login', { email, password });
      console.log('AuthContext: Полный ответ от сервера:', response);
      console.log('AuthContext: Данные ответа:', response.data);
      console.log('AuthContext: user_id в ответе:', response.data.user_id);
      
      if (!response.data.user_id) {
        console.error('AuthContext: user_id отсутствует в ответе:', response.data);
        throw new Error('Сервер не вернул user_id');
      }
      
      const userData = { user_id: response.data.user_id, email };
      console.log('AuthContext: Сохраняем пользователя:', userData);
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error: any) {
      console.error('AuthContext: Ошибка входа:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка входа');
    }
  };

  const register = async (email: string, password: string) => {
    try {
      console.log('AuthContext: Попытка регистрации для:', email);
      const response = await apiClient.post('/register', { email, password });
      console.log('AuthContext: Ответ от сервера:', response.data);
      
      if (!response.data.user_id) {
        throw new Error('Сервер не вернул user_id');
      }
      
      const userData = { user_id: response.data.user_id, email };
      console.log('AuthContext: Сохраняем пользователя:', userData);
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error: any) {
      console.error('AuthContext: Ошибка регистрации:', error);
      throw new Error(error.response?.data?.detail || 'Ошибка регистрации');
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const value = {
    user,
    login,
    register,
    logout,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
